import React, { useEffect } from 'react';
import { func, node, oneOfType, string } from 'prop-types';
import styled from 'styled-components';
import { transparentize } from 'polished';
import Box from '../atoms/Box';

export const CHECKBOX_STATES = {
  CHECKED: 'CHECKED',
  INDETERMINATE: 'INDETERMINATE',
  UNCHECKED: 'UNCHECKED'
};

const HiddenCheckbox = styled('input')(() => ({
  border: 0,
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: '1px',
  margin: '-1px',
  overflow: 'hidden',
  padding: 0,
  position: 'absolute',
  whiteSpace: 'nowrap',
  width: '1px'
}));

const CheckedIcon = styled('svg')(() => ({
  fill: 'none',
  stroke: 'white',
  strokeWidth: '3px',
  strokeLinecap: 'round'
}));

const IndeterminateIcon = styled('svg')(() => ({
  fill: 'none',
  stroke: 'white',
  strokeWidth: '3px',
  strokeLinecap: 'round'
}));

const StyledCheckbox = styled('div')(({ theme, error }) => ({
  display: 'inline-block',
  width: '20px',
  height: '20px',
  marginRight: '8px',
  background: `${error ? transparentize(0.8, theme.colors.red) : 'transparent'}`,
  border: `solid 2px ${error ? theme.colors.red : theme.colors.grey}`,
  borderRadius: theme.radii[0],
  transition: 'transform .3s cubic-bezier(0.175, 0.885, 0.32, 1.275), background .15s, border .2s',
  '&:before': {
    content: '""',
    transition: 'background .3s ease',
    position: 'absolute',
    zIndex: -1,
    background: 'transparent',
    top: '-4px',
    left: '-6px',
    width: 'calc(100% + 12px)',
    height: 'calc(100% + 8px)',
    borderRadius: theme.radii[1]
  },
  [CheckedIcon]: {
    visibility: 'hidden',
    display: 'none'
  },
  [IndeterminateIcon]: {
    visibility: 'hidden',
    display: 'none'
  },
  [`${HiddenCheckbox}:focus + &`]: {
    borderColor: theme.colors.main
  },
  [`${HiddenCheckbox}:focus + &:before`]: {
    background: transparentize(0.9, theme.colors.main),
    boxShadow: `0 0 0 2px ${theme.colors.main}`
  },
  [`${HiddenCheckbox}:checked + &`]: {
    background: theme.colors.main,
    borderColor: theme.colors.main
  },
  [`${HiddenCheckbox}:checked + & ${CheckedIcon}`]: {
    visibility: 'visible',
    display: 'block'
  },
  [`${HiddenCheckbox}:indeterminate + &`]: {
    background: theme.colors.main,
    borderColor: theme.colors.main
  },
  [`${HiddenCheckbox}:indeterminate + & ${IndeterminateIcon}`]: {
    visibility: 'visible',
    display: 'block'
  }
}));

export const CheckBox = ({ value, onChange, label }) => {
  const checkboxRef = React.useRef();

  useEffect(() => {
    if (value === CHECKBOX_STATES.CHECKED) {
      checkboxRef.current.checked = true;
      checkboxRef.current.indeterminate = false;
    } else if (value === CHECKBOX_STATES.UNCHECKED) {
      checkboxRef.current.checked = false;
      checkboxRef.current.indeterminate = false;
    } else if (value === CHECKBOX_STATES.INDETERMINATE) {
      checkboxRef.current.checked = false;
      checkboxRef.current.indeterminate = true;
    }
  }, [value]);

  return (
    <Box as="label" display="inline-flex" alignItems="center" cursor="pointer" position="relative">
      <HiddenCheckbox ref={checkboxRef} type="checkbox" onChange={onChange} />
      <StyledCheckbox>
        <CheckedIcon viewBox="0 0 24 24">
          <polyline points="18 8 11 16 7 12" />
        </CheckedIcon>
        <IndeterminateIcon viewBox="0 0 24 24">
          <line x1="6" y1="13" x2="19" y2="13" />
        </IndeterminateIcon>
      </StyledCheckbox>
      <span>{label}</span>
    </Box>
  );
};

CheckBox.propTypes = {
  value: string,
  onChange: func,
  children: node,
  label: oneOfType([node, string])
};

export default CheckBox;
